/// <amd-module name="Feed/Legacy/Stores/PackageStore" />
import * as Store from "VSS/Reflux/Store";

import { announce, ProgressAnnouncer } from "VSS/Core/Util/Accessibility";
import * as StringUtils from "VSS/Core/Util/String";
import * as Utils_String from "VSS/Core/Util/String";
import { IFilterState } from "VSSUI/Utilities/Filter";

import { findIndex } from "OfficeFabric/Utilities";

import * as Actions from "Feed/Legacy/Actions/Actions";
import {
    buildAnnouncementPackageFilterResults,
    buildAnnouncementListedStatusChangedForPackageVersion
} from "Feed/Legacy/Helpers/AccessibilityHelper";
import { RecycleBinActions } from "Feed/Legacy/Actions/RecycleBinActions";
import { CustomerIntelligenceHelper } from "Feed/Legacy/Helpers/CustomerIntelligenceHelper";
import { FeedView, Package, PackageMetrics, PackageVersion, PackageVersionMetrics } from "Feed/Common/Contracts/Feed";
import { Feed, MinimalPackageVersion } from "Feed/Legacy/WebApi/VSS.CustomFeed.Contracts";
import { HubActionStrings, PerfScenarios, CiConstants, Exceptions } from "Feed/Common/Constants/Constants";
import { IError, IServerError } from "Feed/Common/Types/IError";
import { IHubState, HubAction, HubStateHelpers } from "Feed/Legacy/Types/IHubState";
import { MinimalPackageDetails } from "Feed/Legacy/Protocols/Common/WebApi/PackagingShared.Contracts";
import * as NpmContracts from "Feed/Common/Protocols/Npm/Contracts/Npm";
import { INpmDataService } from "Feed/Legacy/Protocols/Npm/NpmDataService";
import { NpmBatchOperationType } from "Feed/Common/Protocols/Npm/Contracts/Npm";
import { IFeedNuGetDataService } from "Feed/Legacy/Protocols/NuGet/NuGetDataService";
import { NuGetPackagesBatchRequest, NuGetBatchOperationType } from "Feed/Common/Protocols/NuGet/Contracts/NuGet";
import { PackageDetailsRetrievalResult } from "Feed/Common/Contracts/WebPage";
import { IPackageFollowState } from "Feed/Common/Types/IPackageFollowState";
import {
    IPackagePayload,
    IPackagePromotedPayload,
    IPackageListedStatusChangedPayload,
    IPackageListedPayload,
    IPackageDeletedPayload,
    IPackageVersionSelectedPayload,
    IPackageDeprecatedPayload,
    IMultiCommandPayload,
    IFeedRetentionPolicyUpdatedPayload,
    IPackageDependencySelectedPayload
} from "Feed/Legacy/Common/ActionPayloads";
import { IPackageProtocol } from "Feed/Legacy/Protocols/Common/IPackageProtocol";
import { isV2Feed } from "Feed/Legacy/Helpers/FeedCapabilityHelper";
import { HubWebPageDataService } from "Feed/Legacy/DataServices/WebPageDataService";
import { FilterByEnabledProtocolDataService } from "Feed/Legacy/DataServices/FilterByEnabledProtocolDataService";
import { IFeedDataService } from "Feed/Legacy/DataServices/FeedsDataService";
import { PackageModifiedCache } from "Feed/Legacy/Common/PackageModifiedCache";
import * as PackageResources from "Feed/Common/Resources";
import { getPackageMetricsAsync, getPackageVersionMetricsAsync } from "Feed/Legacy/Helpers/PackageMetricsHelper";
import * as PermissionHelper from "Feed/Legacy/Helpers/PermissionHelper";
import { SettingsDataService } from "Feed/Legacy/DataServices/SettingsDataService";
import { IFeedFollowsDataService } from "Feed/Legacy/DataServices/FollowsDataService";
import { PackageFilterBarConstants } from "Feed/Common/Constants/Constants";
import { RecycleBinDataService } from "Feed/Legacy/DataServices/RecycleBinDataService";
import { IFeedProtocolProvider } from "Feed/Legacy/Protocols/ProtocolProvider";
import { SettingsActions } from "Feed/Legacy/Actions/SettingsActions";
import { IVssPageContext } from "VSS/Platform/Context";
import { IFeedPerformanceService } from "Feed/Common/Services/FeedPerformanceService";
import { IVssHistoryService } from "VSS/Platform/History";
import { toHubState } from "Feed/Legacy/Utils/Hub";
import { NavigationHandler } from "Feed/Legacy/Common/NavigationHandler";
import { NotificationSubscription } from "Notifications/Common/Contracts/Notification";
import { getProjectIdForProtocol } from "Feed/Legacy/Helpers/ProjectHelper";

import { FeedHubActions } from "Feed/Core/Redux/FeedHub/FeedHubActions";
import { IReduxStoreService } from "VSS/Redux/StoreService";
import { replaceHistoryPoint } from "Feed/Core/Redux/Navigation/NavigationActions";

// tslint:disable:interface-name
export interface PackageState {
    isLoading: boolean;
    isMetricsLoading: boolean;
    isVersionMetricsLoading: boolean;
    isPackageModifiedLoading: boolean;
    protocolMap: { [key: string]: IPackageProtocol };
    selectedFeed: Feed;
    selectedPackage: Package;
    selectedVersion: PackageVersion;
    feedViews: FeedView[];
    updatedPackageIds: string[];
    packageFollowState: IPackageFollowState;
    messageBarText: string;
    selectedVersions: PackageVersion[];
    /**
     * A map of Package ID (Guid) to boolean, indicating whether the package retention warning
     * message has been dismissed (exists and true) or not (undefined or false)
     */
    retentionMessageSuppressions: { [key: string]: boolean };
    /**
     * List of version to display in grid after filtering
     * When filtering is cleared this will be same as selectedPackage.versions
     */
    filteredVersions: MinimalPackageVersion[];
    retentionPoliciesEnabled: boolean;
    smartDependenciesEnabled: boolean;
    provenanceEnabled: boolean;
    showCreateBadgePanel: boolean;
    showPromoteDialog: boolean;
    packageMetrics: PackageMetrics;
    /* A map of package version id to package version metrics */
    packageVersionMetricsMap: { [key: string]: PackageVersionMetrics };
    isMetricsEnabled: boolean;
}

export class PackageStore extends Store.Store {
    private pageContext: IVssPageContext;

    private get PerformanceService(): IFeedPerformanceService {
        return this.pageContext.getService<IFeedPerformanceService>("IFeedPerformanceService");
    }
    // ***Note***: There is some violation of the 'single-direction' flow in this store.
    // Specifically, the action handlers for operations invoked from the GeneralDialog
    // component need to invoke an action to close the dialog once they have finished
    // handling the Action.
    // We will be moving the processing to 'action-creators' and making the store's simpler.
    // At that point, this undesirable pattern will be removed. In the meantime, don't
    // perpetuate this pattern any further - see saponnu or philho if you feel you need to.

    constructor(
        pageContext: IVssPageContext,
        state: IHubState,
        getSelectedFeedDelegate: () => Feed,
        getFeedViewsDelegate: () => FeedView[]
    ) {
        super("feed-PackageStore");
        this.pageContext = pageContext;
        this._getSelectedFeedDelegate = getSelectedFeedDelegate;
        this._getFeedViewsDelegate = getFeedViewsDelegate;

        this._feedsDataService = this.pageContext.getService<IFeedDataService>("IFeedDataService");
        this._webPageDataService = this.pageContext.getService<HubWebPageDataService>("IFeedWebPageDataService");
        this._recycleBinDataService = this.pageContext.getService<RecycleBinDataService>("IFeedRecycleBinDataService");
        this._filterByEnabledProtocolDataService = this.pageContext.getService<FilterByEnabledProtocolDataService>(
            "IFeedFilterByEnabledProtocolDataService"
        );

        this._currentState = {
            updatedPackageIds: [],
            showCreateBadgePanel: false,
            retentionMessageSuppressions: {},
            // Feature flags
            smartDependenciesEnabled: this._webPageDataService.isSmartDependenciesEnabled(),
            provenanceEnabled: this._webPageDataService.isProvenanceEnabled(),
            isMetricsEnabled: this._webPageDataService.isPackageMetricsEnabled(),
            packageFollowState: {
                isPackageFollowed: false,
                isFollowStateLoaded: false,
                subscriptionId: undefined
            },
            packageVersionMetricsMap: {}
        } as PackageState;

        this.setSelectedFeed();
        this.setFeedViews();

        this.isAnonymousOrPublic = PermissionHelper.isUserAnonymousOrPublic(
            this.pageContext,
            this._currentState.selectedFeed
        );

        if (
            state.action === HubActionStrings.ViewPackage &&
            this._currentState.selectedFeed.upstreamEnabled === false &&
            !!state.upstreamSource
        ) {
            Actions.ErrorEncountered.invoke({
                message: PackageResources.Error_UpstreamSourcesNotEnabled
            } as IError);
        }

        const hubAction = HubStateHelpers.getHubAction(state.action);
        if (hubAction === HubAction.Package || hubAction === HubAction.RecycleBinPackage) {
            this._initializePackageDetailsData();
        }

        this._initializeProtocolMap();
        this._initializeActionListeners();
        this._getFollowState();
    }

    public setHubState(action: string, state: IHubState): void {
        switch (HubStateHelpers.getHubAction(action)) {
            case HubAction.CreateFeed:
            case HubAction.Settings:
            case HubAction.Feed:
            case HubAction.RecycleBin:
                break;
            case HubAction.Package:
            case HubAction.RecycleBinPackage:
                this._handleDirectPackageSelectionAction(state);
                this.emitChanged();
                break;
            default:
                // Error is handled be the feed store
                break;
        }
    }

    public getPackageState(): PackageState {
        return this._currentState;
    }

    public getPackageVersions(): MinimalPackageVersion[] {
        if (!this._versionsFetched) {
            return [];
        }

        if (this._currentState.filteredVersions == null) {
            this._currentState.filteredVersions = this._currentState.selectedPackage.versions;
        }

        return this._currentState.filteredVersions;
    }

    public getRecycleBinPackageVersions(): MinimalPackageVersion[] {
        if (this._recycleBinVersionsFetched) {
            // Removing packages which are now restored or permanently deleted
            const tempVersions = this._currentState.selectedPackage.versions;

            for (let i = 0; i < tempVersions.length; i++) {
                if (!tempVersions[i].isDeleted) {
                    tempVersions.splice(i, 1);
                    i--;
                }
            }

            return tempVersions;
        } else {
            return [];
        }
    }

    private _initializeActionListeners(): void {
        Actions.PackageDependencySelected.addListener(this._handlePackageDependencySelectedAction);
        Actions.PackageSelected.addListener(this._handlePackageSelectedAction);
        RecycleBinActions.PackageSelected.addListener(this._handleRecycleBinPackageSelectedAction);
        Actions.PackageVersionSelected.addListener(this._handlePackageVersionSelectedAction);
        Actions.PackageModified.addListener(this._handlePackageVersionModifiedAction);
        Actions.PackageVersionPromoted.addListener(this._handlePackageVersionPromotedAction);
        Actions.PackageVersionDeprecated.addListener(this._handlePackageVersionDeprecatedAction);
        Actions.PackageListedStatusChanged.addListener(this._handlePackageListedStatusChangedAction);
        Actions.PackageFollowClicked.addListener(this._setFollowState);
        Actions.VersionsPivotSelected.addListener(this._handleVersionsPivotSelectedAction);
        RecycleBinActions.VersionsPivotSelected.addListener(this._handleRecycleBinVersionsPivotSelectedAction);
        Actions.VersionSelectionChanged.addListener(this._handleVersionSelectionChangedAction);
        Actions.PackagesDeleted.addListener(this._handlePackagesDeletedAction);
        Actions.PackageListedChanged.addListener(this._handlePackageListedChangedAction);
        Actions.PackageVersionsFiltersChanged.addListener(this.handlePackageVersionsFiltersChanged);
        SettingsActions.FeedRetentionPolicyUpdated.addListener(this._handleFeedRetentionPolicyUpdated);

        RecycleBinActions.PackagesRestoredToFeed.addListener(this._handlePackagesRestoredToFeedAction);
        RecycleBinActions.PackagesPermanentDeleted.addListener(this._handlePackagesPermanentDeletedAction);

        Actions.ToggleCreateBadgePanel.addListener(this._handleToggleCreateBadgePanelAction);
        Actions.TogglePromoteDialog.addListener(this._handleTogglePromoteDialogAction);
        Actions.DismissPackageRetentionMessage.addListener(this.handleDismissPackageRetentionMessage);
    }

    public dispose(): void {
        Actions.PackageDependencySelected.removeListener(this._handlePackageDependencySelectedAction);
        Actions.PackageSelected.removeListener(this._handlePackageSelectedAction);
        RecycleBinActions.PackageSelected.removeListener(this._handleRecycleBinPackageSelectedAction);
        Actions.PackageVersionSelected.removeListener(this._handlePackageVersionSelectedAction);
        Actions.PackageModified.removeListener(this._handlePackageVersionModifiedAction);
        Actions.PackageVersionPromoted.removeListener(this._handlePackageVersionPromotedAction);
        Actions.PackageVersionDeprecated.removeListener(this._handlePackageVersionDeprecatedAction);
        Actions.PackageListedStatusChanged.removeListener(this._handlePackageListedStatusChangedAction);
        Actions.PackageFollowClicked.removeListener(this._setFollowState);
        Actions.VersionsPivotSelected.removeListener(this._handleVersionsPivotSelectedAction);
        RecycleBinActions.VersionsPivotSelected.removeListener(this._handleRecycleBinVersionsPivotSelectedAction);
        Actions.VersionSelectionChanged.removeListener(this._handleVersionSelectionChangedAction);
        Actions.PackagesDeleted.removeListener(this._handlePackagesDeletedAction);
        Actions.PackageListedChanged.removeListener(this._handlePackageListedChangedAction);
        Actions.PackageVersionsFiltersChanged.removeListener(this.handlePackageVersionsFiltersChanged);
        SettingsActions.FeedRetentionPolicyUpdated.removeListener(this._handleFeedRetentionPolicyUpdated);

        RecycleBinActions.PackagesRestoredToFeed.removeListener(this._handlePackagesRestoredToFeedAction);
        RecycleBinActions.PackagesPermanentDeleted.removeListener(this._handlePackagesPermanentDeletedAction);

        Actions.ToggleCreateBadgePanel.removeListener(this._handleToggleCreateBadgePanelAction);
        Actions.TogglePromoteDialog.removeListener(this._handleTogglePromoteDialogAction);
        Actions.DismissPackageRetentionMessage.removeListener(this.handleDismissPackageRetentionMessage);
    }

    private setSelectedFeed(): Feed {
        const feed = this._getSelectedFeedDelegate();
        this._currentState.selectedFeed = feed;
        if (feed != null && feed.deletedDate == null) {
            this._ensureRetentionPolicyLoaded(feed);
        }

        return feed;
    }

    private setFeedViews(): void {
        this._currentState.feedViews = this._getFeedViewsDelegate();
    }

    private async _ensureRetentionPolicyLoaded(feed: Feed): Promise<Feed> {
        if (
            PermissionHelper.hasAccessToBaseFeed(feed) &&
            this._currentState.retentionPoliciesEnabled &&
            feed.retentionPolicy === void 0
        ) {
            const feedDataService = this.pageContext.getService<IFeedDataService>("IFeedDataService");
            try {
                const projectId = feed.project ? feed.project.id : null;
                feed.retentionPolicy = await feedDataService.getFeedRetentionPolicy(projectId, feed.id);
            } catch (err) {
                const message = `${PackageResources.Error_ErrorLoadingRetentionPolicy} Error: ${err && err.message}`;
                // It should be ok to invoke an action here, since it's on the
                // continuation thread, not the main 'action-invoked' thread
                Actions.ErrorEncountered.invoke({ message });
            }

            if (feed.id === this._currentState.selectedFeed.id) {
                this.emitChanged();
            }
        }

        return feed;
    }

    private _initializePackageDetailsData() {
        const packageDetailsResult = this._filterByEnabledProtocolDataService.getPackageDetailsResult();

        switch (packageDetailsResult.result) {
            case PackageDetailsRetrievalResult.Success:
                this._currentState.selectedPackage = packageDetailsResult.package;
                this._currentState.selectedVersion = packageDetailsResult.packageVersion;
                this._updatePackageMetricsAsync();
                this._updatePackageVersionMetricsAsync();
                this._navigateToCurrentPackage();
                break;
            case PackageDetailsRetrievalResult.NotFound:
            case PackageDetailsRetrievalResult.NotFoundWithSamePackageCase:
            case PackageDetailsRetrievalResult.NotFoundWithSameVersionCase:
                const state = this.NavigationService.getState() as IHubState;
                CustomerIntelligenceHelper.publishEvent(this.pageContext, CiConstants.PackageLoadError, {
                    ...state,
                    packageDetailsRetrievalResult: packageDetailsResult.result
                });
                Actions.ErrorEncountered.invoke({
                    message: PackageResources.Error_UrlPackageNonFound,
                    link: () => Actions.FeedBreadcrumbSelected.invoke(null),
                    linkText: PackageResources.Error_GoToPackageListLink
                } as IError);
                break;
            case PackageDetailsRetrievalResult.Error:
            default:
                CustomerIntelligenceHelper.publishEvent(this.pageContext, CiConstants.PackageLoadError, {
                    ...state,
                    packageDetailsRetrievalResult: packageDetailsResult.result
                });
                Actions.ErrorEncountered.invoke({
                    message: PackageResources.Error_ErrorLoadingPackageDetailsInDataProvider,
                    isCritical: true,
                    details: packageDetailsResult.errorMessage,
                    link: () => Actions.FeedBreadcrumbSelected.invoke(null),
                    linkText: PackageResources.Error_GoToPackageListLink
                } as IError);
                break;
        }
    }

    // replace any guids in the uri with string representations
    private _navigateToCurrentPackage(): void {
        const navState: { [key: string]: string } = this.NavigationService.getState();
        navState._a = navState._a != HubActionStrings.RecycleBinPackage ? HubActionStrings.ViewPackage : navState._a;
        navState.feed = this._currentState.selectedFeed.fullyQualifiedName;
        navState.package = this._currentState.selectedPackage.name;
        navState.version = this._currentState.selectedVersion.version;
        navState.protocolType = this._currentState.selectedPackage.protocolType;
        // since version is now set, preferRelease has no meaning and can be cleared from the url
        navState.preferRelease = undefined;
        navState.upstreamSource = undefined;
        this.NavigationService.replaceState(navState, null, null, toHubState(navState));
    }

    private _initializeProtocolMap(): void {
        this._currentState.protocolMap = this.ProtocolProvider.getEnabledProtocolTypes();
    }

    private handlePackageVersionsFiltersChanged = (filterState: IFilterState): void => {
        this._currentState.filteredVersions = this._currentState.selectedPackage.versions;
        if (filterState) {
            if (filterState[PackageFilterBarConstants.KeywordFilterKey]) {
                const keywordSearchTerm = filterState[PackageFilterBarConstants.KeywordFilterKey].value;
                this._currentState.filteredVersions = this._currentState.filteredVersions.filter(
                    x => x.version.indexOf(keywordSearchTerm) >= 0
                );
            }

            if (filterState[PackageFilterBarConstants.ViewFilterKey]) {
                // Currently on V1 feeds all cached npm packages are in all views.
                if (
                    isV2Feed(this._currentState.selectedFeed) ||
                    (this._currentState.selectedPackage.isCached !== true &&
                        this._currentState.selectedPackage.versions[0].isCachedVersion !== true)
                ) {
                    let viewSearchTerm = filterState[PackageFilterBarConstants.ViewFilterKey].value[0];
                    if (viewSearchTerm) {
                        viewSearchTerm = viewSearchTerm.replace("@", "");
                        this._currentState.filteredVersions = this._currentState.filteredVersions.filter(
                            x => x.views.filter(y => y.name === viewSearchTerm).length > 0
                        );
                    }
                }
            }
        }

        const announcement = buildAnnouncementPackageFilterResults(this._currentState.filteredVersions);
        announce(announcement);

        this.emitChanged();
    };

    private _handlePackageSelectedAction = (packageSummary: Package): void => {
        CustomerIntelligenceHelper.publishEvent(this.pageContext, CiConstants.PackageSelected);
        const selectPackageScenario = this.PerformanceService.startScenario(PerfScenarios.PackageSelected);

        this._currentState.selectedPackage = packageSummary;
        this._currentState.isLoading = true;
        this.setSelectedFeed();
        this.setFeedViews();

        const hubState = {
            action: HubActionStrings.ViewPackage,
            feed: this._getFullyQualifiedFeedName(this._currentState.selectedFeed),
            package: packageSummary.name,
            version: packageSummary.versions[0].version,
            protocolType: packageSummary.protocolType
        } as IHubState;
        NavigationHandler.pushHistoryEntry(this.pageContext, hubState);

        const feed = this._currentState.selectedFeed;
        const feedId = this._getFullyQualifiedFeedId(feed);

        const isDeleted = null;
        const isListed = null;
        this._feedsDataService
            .getPackageVersionAsync(feed, packageSummary.id, packageSummary.versions[0].id, isListed, isDeleted)
            .then(
                (version: PackageVersion) => {
                    if (
                        // todo: verify why are getting fully qualified id again?
                        feedId === this._getFullyQualifiedFeedId(this._currentState.selectedFeed) &&
                        packageSummary.id === this._currentState.selectedPackage.id
                    ) {
                        this._currentState.selectedVersion = PackageModifiedCache.applyCacheByPackageVersion(
                            version
                        ) as PackageVersion;
                        this._currentState.isLoading = false;

                        document.title = this._getHubTitle();

                        if (this._currentState.updatedPackageIds && this._currentState.updatedPackageIds.length > 0) {
                            Actions.PackageListUpdated.invoke(this._currentState.updatedPackageIds);
                            this._currentState.updatedPackageIds = [];
                        }

                        this.emitChanged();

                        this._getFollowState();
                        this.PerformanceService.endScenario(selectPackageScenario);
                    } else {
                        this.PerformanceService.endScenario(selectPackageScenario);
                    }

                    return version;
                },
                (error: IServerError) => {
                    this.PerformanceService.endScenario(selectPackageScenario);
                    this._currentState.isLoading = false;
                    this.emitChanged();

                    let displayMessage = PackageResources.Error_ErrorLoadingPackageDetails;
                    if (error.serverError.typeKey === Exceptions.PackageNotFoundException) {
                        displayMessage = PackageResources.Error_UrlPackageNonFound;
                    }

                    Actions.ErrorEncountered.invoke({
                        message: displayMessage,
                        isCritical: true,
                        details: error.message,
                        link: () => Actions.FeedBreadcrumbSelected.invoke(null),
                        linkText: PackageResources.Error_GoToPackageListLink
                    } as IError);

                    return null;
                }
            )
            .then((version: PackageVersion) => {
                if (version == null) {
                    return;
                }

                // todo: verify why are getting fully qualified id again?
                const feedId1 = this._getFullyQualifiedFeedId(feed);
                this._updatePackageMetricsAsync();
                this._updatePackageVersionMetricsAsync();
            });

        this._resetPackageVersions();
        this.emitChanged();
    };

    private _handlePackageDependencySelectedAction = (payload: IPackageDependencySelectedPayload): void => {
        CustomerIntelligenceHelper.publishEvent(this.pageContext, CiConstants.PackageDependencySelected);
        const selectPackagDependencyScenario = this.PerformanceService.startScenario(
            PerfScenarios.PackageDependencySelected
        );

        const dependency = payload.dependency;

        this._currentState.selectedPackage = {
            id: dependency.packageId,
            name: dependency.name,
            normalizedName: dependency.normalizedPackageName,
            protocolType: dependency.protocolType,
            versions: []
        } as Package;
        this._currentState.isLoading = true;
        this.setSelectedFeed();
        this.setFeedViews();

        const hubState = {
            action: HubActionStrings.ViewPackage,
            feed: this._getFullyQualifiedFeedName(this._currentState.selectedFeed),
            package: dependency.name,
            version: null,
            protocolType: dependency.protocolType
        } as IHubState;
        NavigationHandler.pushHistoryEntry(this.pageContext, hubState);

        const feed = this._currentState.selectedFeed;
        const feedId = this._getFullyQualifiedFeedId(feed);

        const isDeleted = PermissionHelper.isDeleted(feed);
        const isListed = null;
        this._feedsDataService
            .getPackageVersionAsync(feed, dependency.packageId, dependency.packageVersionId, isListed, isDeleted)
            .then(
                (version: PackageVersion) => {
                    // Only do the following in case the selected package or selected feed haven't changed
                    if (
                        feedId === this._getFullyQualifiedFeedId(this._currentState.selectedFeed) &&
                        dependency.packageId === this._currentState.selectedPackage.id
                    ) {
                        this._currentState.selectedVersion = PackageModifiedCache.applyCacheByPackageVersion(
                            version
                        ) as PackageVersion;
                        this._currentState.isLoading = false;

                        // Update with the version information we just retrieved
                        hubState.version = this._currentState.selectedVersion.version;
                        document.title = this._getHubTitle();

                        if (this._currentState.updatedPackageIds && this._currentState.updatedPackageIds.length > 0) {
                            Actions.PackageListUpdated.invoke(this._currentState.updatedPackageIds);
                            this._currentState.updatedPackageIds = [];
                        }

                        this.emitChanged();

                        this._getFollowState();
                        this.PerformanceService.endScenario(selectPackagDependencyScenario);
                    } else {
                        this.PerformanceService.endScenario(selectPackagDependencyScenario);
                    }
                },
                (error: IServerError) => {
                    this.PerformanceService.endScenario(selectPackagDependencyScenario);
                    this._currentState.isLoading = false;
                    this.emitChanged();

                    let displayMessage = PackageResources.Error_ErrorLoadingPackageDetails;
                    if (error.serverError.typeKey === Exceptions.PackageNotFoundException) {
                        displayMessage = PackageResources.Error_UrlPackageNonFound;
                    }

                    Actions.ErrorEncountered.invoke({
                        message: displayMessage,
                        isCritical: true,
                        details: error.message,
                        link: () => Actions.PackageSelected.invoke(payload.originPackage),
                        linkText: PackageResources.Error_GoBackToPackageLink
                    } as IError);
                }
            );

        this._resetPackageVersions();
        this.emitChanged();
    };

    private _handleRecycleBinPackageSelectedAction = (packageSummary: Package): void => {
        CustomerIntelligenceHelper.publishEvent(this.pageContext, CiConstants.RecycleBinPackageSelected);
        const selectPackageScenario = this.PerformanceService.startScenario(PerfScenarios.RecycleBinPackageSelected);

        this._currentState.selectedPackage = packageSummary;
        this._currentState.isLoading = true;
        const feed = this.setSelectedFeed();
        this.setFeedViews();

        const hubState = {
            action: HubActionStrings.RecycleBinPackage,
            feed: this._getFullyQualifiedFeedName(feed),
            package: packageSummary.name,
            version: packageSummary.versions[0].version,
            protocolType: packageSummary.protocolType
        } as IHubState;
        NavigationHandler.pushHistoryEntry(this.pageContext, hubState);

        this._currentState.selectedVersion = packageSummary.versions[0] as PackageVersion;

        document.title = this._getHubTitle();
        this._currentState.isLoading = false;

        this.PerformanceService.endScenario(selectPackageScenario);
        this._resetPackageVersions();
        this.emitChanged();
    };

    private _handlePackageListedStatusChangedAction = (payload: IPackageListedStatusChangedPayload) => {
        if (payload.packageId && !payload.isDeleted) {
            const index = this._currentState.updatedPackageIds.indexOf(payload.packageId);
            if (index === -1) {
                this._currentState.updatedPackageIds.push(payload.packageId);
            } else {
                this._currentState.updatedPackageIds.splice(index, 1);
            }
        }
    };

    private _handlePackageVersionSelectedAction = (payload: IPackageVersionSelectedPayload) => {
        CustomerIntelligenceHelper.publishEvent(this.pageContext, CiConstants.PackageVersionSelectedNew);
        const selectVersionScenario = this.PerformanceService.startScenario(PerfScenarios.PackageVersionSelectedNew);

        if (this._currentState.selectedVersion.version !== payload.version.version) {
            this._currentState.selectedVersion = PackageModifiedCache.applyCacheByPackageVersion(
                payload.version
            ) as PackageVersion;

            const feedId = this._getFullyQualifiedFeedId(this._currentState.selectedFeed);

            if (payload.viewName == null) {
                this._currentState.selectedFeed.view = null;
            } else if (payload.viewName) {
                const viewIndex = findIndex(this._currentState.feedViews, (v: FeedView) => v.name == payload.viewName);
                this._currentState.selectedFeed.view = this._currentState.feedViews[viewIndex];
            }

            const hubState = {
                action: HubActionStrings.ViewPackage,
                feed: this._getFullyQualifiedFeedName(this._currentState.selectedFeed),
                package: this._currentState.selectedPackage.name,
                version: payload.version.version,
                protocolType: this._currentState.selectedPackage.protocolType
            } as IHubState;
            NavigationHandler.pushHistoryEntry(this.pageContext, hubState);

            document.title = this._getHubTitle();

            this.emitChanged();
        }

        this.PerformanceService.endScenario(selectVersionScenario);
    };

    // Get aggregated package ID metrics
    private async _updatePackageMetricsAsync() {
        if (!this._currentState.isMetricsEnabled) {
            return;
        }
        this._currentState.isMetricsLoading = true;
        const projectId = this._currentState.selectedFeed.project ? this._currentState.selectedFeed.project.id : null;
        getPackageMetricsAsync(
            projectId,
            this._currentState.selectedFeed.fullyQualifiedId,
            [this._currentState.selectedPackage.id],
            this._feedsDataService
        ).then(
            (packageIdMetrics: { [key: string]: PackageMetrics }) => {
                this._currentState.packageMetrics = packageIdMetrics[this._currentState.selectedPackage.id];
                this._currentState.isMetricsLoading = false;
                this.emitChanged();
            },
            Error => {
                this._currentState.packageMetrics = null;
                this._currentState.isMetricsLoading = false;
                this.emitChanged();
            }
        );
    }

    private async _updatePackageVersionMetricsAsync() {
        if (!this._currentState.isMetricsEnabled) {
            return;
        }
        this._currentState.isVersionMetricsLoading = true;
        const projectId = this._currentState.selectedFeed.project ? this._currentState.selectedFeed.project.id : null;
        getPackageVersionMetricsAsync(
            projectId,
            this._currentState.selectedFeed.fullyQualifiedId,
            this._currentState.selectedPackage.id,
            this._currentState.selectedPackage.versions != null
                ? this._currentState.selectedPackage.versions.map((ver: PackageVersion) => ver.id)
                : [],
            this._feedsDataService
        ).then(
            (packageVersionMetrics: { [key: string]: PackageVersionMetrics }) => {
                this._currentState.packageVersionMetricsMap = packageVersionMetrics;
                this._currentState.isVersionMetricsLoading = false;
                this.emitChanged();
            },
            Error => {
                this._currentState.packageVersionMetricsMap = null;
                this._currentState.isVersionMetricsLoading = false;
                this.emitChanged();
            }
        );
    }

    private _handleDirectPackageSelectionAction = (hubState: IHubState) => {
        CustomerIntelligenceHelper.publishEvent(this.pageContext, CiConstants.PackageDirectlyLoaded);

        if (this._currentState.selectedPackage && this._currentState.selectedPackage.name === hubState.package) {
            const newVersionIndex = findIndex(
                this._currentState.selectedPackage.versions,
                v => v.version === hubState.version
            );
            if (newVersionIndex !== -1) {
                this._currentState.selectedVersion = this._currentState.selectedPackage.versions[
                    newVersionIndex
                ] as PackageVersion;
            }
        } else {
            window.location.reload();
        }

        this.emitChanged();
    };

    private _getFullyQualifiedFeedId(feed: Feed): string {
        const viewId = feed.view ? `@${feed.view.id}` : "";
        return `${feed.id}${viewId}`;
    }

    private _getFullyQualifiedFeedName(feed: Feed): string {
        const viewName = feed.view ? `@${feed.view.name}` : "";
        return `${feed.name}${viewName}`;
    }

    private _handlePackageVersionPromotedAction = async (payload: IPackagePromotedPayload) => {
        // Versions list currently only supports single promote.
        if (this._currentState.selectedVersion) {
            const { selectedFeed, selectedPackage, selectedVersion } = this._currentState;
            const { minimalPackageDetails, promotedView } = payload;
            const protocolType = minimalPackageDetails[0].protocolType;

            const scenario = this.PerformanceService.startScenario(PerfScenarios.PackagePromoted);

            this._currentState.isLoading = true;
            this.emitChanged();

            try {
                announce(
                    Utils_String.format(
                        PackageResources.PackageVersionPromoted_AnnouncementStart,
                        minimalPackageDetails[0].id,
                        minimalPackageDetails[0].version,
                        promotedView.name
                    )
                );

                const provider = this.ProtocolProvider.get(protocolType, true);
                const projectId = getProjectIdForProtocol(selectedFeed, provider);
                await provider.promotePackageVersions(projectId, selectedFeed, promotedView, minimalPackageDetails);

                const settingsDataService = this.pageContext.getService<SettingsDataService>(
                    "IFeedSettingsDataService"
                );
                const fullyQualifiedFeedId = Utils_String.format("{0}@{1}", selectedFeed.id, promotedView.id);
                settingsDataService.setMruFullyQualifiedFeedId(fullyQualifiedFeedId);

                const index = findIndex(
                    selectedPackage.versions,
                    version => version.version === minimalPackageDetails[0].version
                );
                selectedPackage.versions[index].views.push(promotedView);
                selectedVersion.views.push(promotedView);
                this.emitChanged();

                this.PerformanceService.endScenario(scenario);
                announce(
                    Utils_String.format(
                        PackageResources.PackageVersionPromoted_AnnouncementEnd,
                        minimalPackageDetails[0].id,
                        minimalPackageDetails[0].version,
                        promotedView.name
                    )
                );
            } catch (error) {
                this.PerformanceService.endScenario(scenario);
                const message = Utils_String.format(PackageResources.Error_PromotionFailedForProtocol, protocolType);
                Actions.ErrorEncountered.invoke({ message, isCritical: true });
            } finally {
                this._currentState.isLoading = false;
                this._handleTogglePromoteDialogAction(false);
            }
        }
    };

    private _handlePackageVersionDeprecatedAction = (payload: IPackageDeprecatedPayload): void => {
        if (payload.selectedVersions) {
            CustomerIntelligenceHelper.publishEvent(this.pageContext, CiConstants.MultiplePackageVersionsDeprecated);
            const deprecateScenario = this.PerformanceService.startScenario(
                PerfScenarios.MultiplePackageVersionsDeprecated
            );
            const npmPackageDetails = [];
            payload.selectedVersions.forEach((version: PackageVersion) => {
                npmPackageDetails.push({
                    id: this._currentState.selectedPackage.name,
                    version: version.version
                });
            });

            const npmDataService = this.pageContext.getService<INpmDataService>("INpmDataService");
            const deprecatePackageVersionDetails = {
                data: { message: payload.message ? payload.message : "" } as NpmContracts.BatchDeprecateData,
                operation: NpmBatchOperationType.Deprecate,
                packages: npmPackageDetails
            } as NpmContracts.NpmPackagesBatchRequest;

            let announceStart = PackageResources.NpmDeprecate_ProgressStarted;
            let announceEnd = PackageResources.NpmDeprecate_ProgressEnded;
            let announceError = PackageResources.NpmDeprecate_ProgressFailed;
            if (payload.message == null) {
                announceStart = PackageResources.NpmDeprecate_ProgressEnded_Undeprecate;
                announceEnd = PackageResources.NpmDeprecate_ProgressEnded_Undeprecate;
                announceError = PackageResources.NpmDeprecate_ProgressFailed_Undeprecate;
            }

            const updatePromise = npmDataService
                .updatePackages(deprecatePackageVersionDetails, this._currentState.selectedFeed)
                .then(
                    () => {
                        if (payload.selectedVersions) {
                            payload.selectedVersions.forEach((version: PackageVersion) => {
                                version.protocolMetadata.data.deprecated = payload.message;
                                if (this._currentState.selectedVersion.id === version.id) {
                                    this._currentState.selectedVersion.protocolMetadata.data.deprecated =
                                        payload.message;
                                }
                            });
                            this.emitChanged();
                        }
                        Actions.PackageVersionDeprecatedCompleted.invoke({}); // See ***Note*** above
                        this.PerformanceService.endScenario(deprecateScenario);
                    },
                    err => {
                        Actions.PackageVersionDeprecatedCompleted.invoke({}); // See ***Note*** above
                        this.PerformanceService.endScenario(deprecateScenario);

                        if (err.status === 403) {
                            Actions.ErrorEncountered.invoke({
                                message: PackageResources.Error_ErrorUserUnauthorized,
                                details: err
                            } as IError);
                        } else {
                            Actions.ErrorEncountered.invoke({
                                message: err.message,
                                isCritical: true,
                                details: err
                            } as IError);
                        }
                    }
                );

            ProgressAnnouncer.forPromise(updatePromise, {
                announceStartMessage: announceStart,
                announceEndMessage: announceEnd,
                announceErrorMessage: announceError
            });
        } else if (payload.selectedVersion && !payload.selectedPackages) {
            // This is the handling for the single dialog.
            const index = findIndex(
                this._currentState.selectedPackage.versions,
                (v: PackageVersion) => v.id === payload.selectedVersion.id
            );
            const version = this._currentState.selectedPackage.versions[index] as PackageVersion;
            if (version.protocolMetadata && version.protocolMetadata.data) {
                version.protocolMetadata.data.deprecated = payload.message;
            }

            if (version.id === this._currentState.selectedVersion.id) {
                this._currentState.selectedVersion.protocolMetadata.data.deprecated = payload.message;
            }

            this.emitChanged();
        }
    };

    private _handlePackageVersionModifiedAction = (packagePayload: IPackagePayload): void => {
        this._currentState.isPackageModifiedLoading = false;

        if (
            this._currentState.selectedPackage &&
            this._currentState.selectedPackage.id === packagePayload.packageSummary.id
        ) {
            this._currentState.selectedPackage = packagePayload.packageSummary;
        }

        const modifiedVersion = packagePayload.packageVersion;
        PackageModifiedCache.packageModifiedCache[modifiedVersion.id] = modifiedVersion;
        if (this._currentState.selectedVersion && this._currentState.selectedVersion.id === modifiedVersion.id) {
            this._currentState.selectedVersion = modifiedVersion;
        }

        this.emitChanged();
    };

    private _getFollowState(): void {
        if (this.isAnonymousOrPublic) {
            this._currentState.packageFollowState.isFollowStateLoaded = false;
            this._currentState.packageFollowState.isPackageFollowed = false;
            this._currentState.packageFollowState.subscriptionId = undefined;
            this.emitChanged();
            return;
        }

        this.FollowsDataService.getFollowState(this._currentState.selectedPackage, this._currentState.selectedFeed)
            .then((subscription: NotificationSubscription) => {
                this.setPackageFollowState(subscription);
                this.emitChanged();
            })
            .catch(() => {
                this._currentState.packageFollowState.isFollowStateLoaded = false;
                this._currentState.packageFollowState.isPackageFollowed = false;
                this._currentState.packageFollowState.subscriptionId = undefined;
                this.emitChanged();
            });
    }

    private _setFollowState = (follow: boolean) => {
        if (follow) {
            this.FollowsDataService.follow(this._currentState.selectedPackage, this._currentState.selectedFeed).then(
                (subscription: NotificationSubscription) => {
                    this.setPackageFollowState(subscription);
                    this.emitChanged();
                }
            );
        } else {
            this.FollowsDataService.unfollow(this._currentState.packageFollowState.subscriptionId).then(() => {
                this._currentState.packageFollowState.isFollowStateLoaded = true;
                this._currentState.packageFollowState.isPackageFollowed = false;
                this._currentState.packageFollowState.subscriptionId = undefined;
                this.emitChanged();
            });
        }
    };

    private setPackageFollowState(subscription: NotificationSubscription): void {
        this._currentState.packageFollowState.isFollowStateLoaded = true;
        this._currentState.packageFollowState.isPackageFollowed = subscription != null;
        this._currentState.packageFollowState.subscriptionId = subscription && subscription.id;
    }

    private get FollowsDataService(): IFeedFollowsDataService {
        return this.pageContext.getService<IFeedFollowsDataService>("IFeedFollowsDataService");
    }

    private _handleVersionsPivotSelectedAction = () => {
        CustomerIntelligenceHelper.publishEvent(this.pageContext, CiConstants.PackageVersionsPivotSelected);

        if (!this._versionsFetched) {
            const selectPivotScenario = this.PerformanceService.startScenario(
                PerfScenarios.PackageVersionsPivotSelected
            );
            this._currentState.isLoading = true;

            const feed = this._currentState.selectedFeed;
            const packageId = this._currentState.selectedPackage.id;
            const isListed = null;
            const isDeleted = PermissionHelper.isDeleted(feed);
            const feedId = this._getFullyQualifiedFeedId(feed);
            const projectId = this._currentState.selectedFeed.project
                ? this._currentState.selectedFeed.project.id
                : null;
            this._feedsDataService
                .getPackageVersionsAsync(projectId, feedId, packageId, isListed, isDeleted)
                .then((versions: PackageVersion[]) => {
                    if (
                        feed.id === this._currentState.selectedFeed.id &&
                        packageId === this._currentState.selectedPackage.id
                    ) {
                        this._currentState.selectedPackage.versions = versions;
                        this._versionsFetched = true;
                        this.PerformanceService.endScenario(selectPivotScenario);
                    } else {
                        this.PerformanceService.endScenario(selectPivotScenario);
                    }
                    this._currentState.isLoading = false;
                    this.emitChanged();
                    this._updatePackageVersionMetricsAsync();
                });
        }

        this._currentState.selectedVersions = [];
        this.emitChanged();
    };

    private _handleRecycleBinVersionsPivotSelectedAction = (): void => {
        CustomerIntelligenceHelper.publishEvent(this.pageContext, CiConstants.RecycleBinPackageVersionsPivotSelected);
        this._currentState.messageBarText = null;
        if (!this._recycleBinVersionsFetched) {
            const selectPivotScenario = this.PerformanceService.startScenario(
                PerfScenarios.RecycleBinPackageVersionsPivotSelected
            );
            this._currentState.isLoading = true;

            const feed = this._currentState.selectedFeed;
            const packageId = this._currentState.selectedPackage.id;
            this._recycleBinDataService.getPackageVersionsAsync(feed, packageId).then(
                (versions: PackageVersion[]) => {
                    if (
                        feed.id === this._currentState.selectedFeed.id &&
                        packageId === this._currentState.selectedPackage.id
                    ) {
                        this._currentState.selectedPackage.versions = versions;
                        this._recycleBinVersionsFetched = true;
                        this.PerformanceService.endScenario(selectPivotScenario);
                    } else {
                        this.PerformanceService.endScenario(selectPivotScenario);
                    }
                    this._currentState.isLoading = false;
                    this.emitChanged();
                },
                (error: Error) => {
                    this._currentState.isLoading = false;
                    this.PerformanceService.endScenario(selectPivotScenario);
                    Actions.ErrorEncountered.invoke({
                        message: error.message,
                        isCritical: true
                    } as IError);
                }
            );
        }

        this._currentState.selectedVersions = [];
        this.emitChanged();
    };

    private _handleVersionSelectionChangedAction = (versions: PackageVersion[]): void => {
        this._currentState.selectedVersions = versions;
        this.emitChanged();
    };

    private _handlePackagesDeletedAction = async (payload: IPackageDeletedPayload) => {
        // This handler only deals with the case where we're invoked on
        // one or more versions of a single package. The handler for deleting
        // package versions across packages is in FeedStore
        if (!payload.selectedVersions || !payload.protocolType) {
            return;
        }

        const feed = this._currentState.selectedFeed;
        const pkg = this._currentState.selectedPackage;
        const versions = payload.selectedVersions;
        const singleVersion = versions.length === 1;

        CustomerIntelligenceHelper.publishEvent(
            this.pageContext,
            singleVersion ? CiConstants.PackageVersionDeleted : CiConstants.MultiplePackageVersionsDeleted
        );
        const perfScenario = this.PerformanceService.startScenario(
            singleVersion ? PerfScenarios.PackageVersionDeleted : PerfScenarios.MultiplePackageVersionsDeleted
        );
        Object.assign(perfScenario.properties, { protocolType: payload.protocolType });

        try {
            const protocol = this.ProtocolProvider.get(payload.protocolType, /*throwIfMissing*/ true);
            const projectId = getProjectIdForProtocol(feed, protocol);
            await protocol.deletePackageVersions(projectId, feed, pkg, versions);
            this._lieToUserAfterPackageDeletion(payload);
            this.PerformanceService.endScenario(perfScenario);
        } catch (error) {
            this.PerformanceService.endScenario(perfScenario);
            Actions.ErrorEncountered.invoke(<IError>{
                message: error.message,
                isCritical: true
            });
        } finally {
            this._currentState.selectedVersions = [];
            this.emitChanged();
            Actions.PackageDeletedCompleted.invoke({}); // See ***Note*** above
        }
    };

    private _handlePackagesRestoredToFeedAction = async (payload: IMultiCommandPayload) => {
        if (!payload.selectedVersions) {
            return;
        }

        const feed = this._currentState.selectedFeed;
        const pkg = this._currentState.selectedPackage;
        const versions = payload.selectedVersions;
        const singleVersion = versions.length === 1;
        let protocol: IPackageProtocol;

        CustomerIntelligenceHelper.publishEvent(
            this.pageContext,
            singleVersion
                ? CiConstants.RecycleBinPackageVersionRestore
                : CiConstants.MultiplePackageVersionsRestoredToFeed
        );
        const perfScenario = this.PerformanceService.startScenario(
            singleVersion
                ? PerfScenarios.RecycleBinPackageVersionRestore
                : PerfScenarios.MultiplePackageVersionsRestoredToFeed
        );

        try {
            this._currentState.isLoading = true;
            this.emitChanged();
            protocol = this.ProtocolProvider.get(pkg.protocolType, /*throwIfMissing*/ true);
            const projectId = getProjectIdForProtocol(feed, protocol);
            await protocol.restorePackageVersionsToFeed(projectId, feed, pkg, versions);
            this._lieToUserAfterRecycleBinAction(payload, PackageResources.RecycleBinRestoredMessage);
            this.PerformanceService.endScenario(perfScenario);
            if (payload.selectedVersions.length === 1) {
                announce(
                    Utils_String.format(
                        PackageResources.PackageRestored_Announcement_One,
                        payload.selectedVersions[0].version
                    )
                );
            } else {
                announce(
                    Utils_String.format(PackageResources.PackageRestored_Announcement, payload.selectedVersions.length)
                );
            }
        } catch (error) {
            this.PerformanceService.endScenario(perfScenario);
            if (!protocol) {
                this._currentState.messageBarText = PackageResources.PackageRestoreNotSupportedOnThisProtocol;
                return;
            }
            Actions.ErrorEncountered.invoke({
                message: (error.serverError && error.serverError.reason) || error.message,
                isCritical: true
            });
        } finally {
            this._currentState.isLoading = false;
            this._currentState.selectedVersions = [];
            this.emitChanged();
        }
    };

    private _handlePackagesPermanentDeletedAction = async (payload: IMultiCommandPayload): Promise<void> => {
        if (!payload.selectedVersions) {
            return;
        }

        const feed = this._currentState.selectedFeed;
        const pkg = this._currentState.selectedPackage;
        const versions = payload.selectedVersions;
        const singleVersion = versions.length === 1;
        let protocol: IPackageProtocol;

        CustomerIntelligenceHelper.publishEvent(
            this.pageContext,
            singleVersion
                ? CiConstants.RecycleBinPackageVersionPermanentDelete
                : CiConstants.MultiplePackageVersionsPermanentDeleted
        );
        const perfScenario = this.PerformanceService.startScenario(
            singleVersion
                ? PerfScenarios.RecycleBinPackageVersionPermanentDelete
                : PerfScenarios.MultiplePackageVersionsPermanentDeleted
        );

        try {
            this._currentState.isLoading = true;
            this.emitChanged();
            protocol = this.ProtocolProvider.get(pkg.protocolType, /*throwIfMissing*/ true);
            const projectId = getProjectIdForProtocol(feed, protocol);
            await protocol.permanentlyDeletePackageVersions(projectId, feed, pkg, versions);
            this._lieToUserAfterRecycleBinAction(payload, PackageResources.RecycleBinPermanentDeletedMessage);
            this.PerformanceService.endScenario(perfScenario);
        } catch (error) {
            this.PerformanceService.endScenario(perfScenario);
            if (!protocol) {
                this._currentState.messageBarText = PackageResources.PermanentDeleteNotSupportedOnThisProtocol;
                return;
            }
            Actions.ErrorEncountered.invoke({
                message: StringUtils.format(PackageResources.DeleteDialog_MultiDeleteFailed, pkg.protocolType),
                isCritical: true
            });
        } finally {
            RecycleBinActions.PackagesPermanentDeletedCompleted.invoke({}); // See ***Note*** above
            this._currentState.isLoading = false;
            this._currentState.selectedVersions = [];
            this.emitChanged();
        }
    };

    private _handlePackageListedChangedAction = (payload: IPackageListedPayload) => {
        if (payload.selectedVersions) {
            CustomerIntelligenceHelper.publishEvent(
                this.pageContext,
                CiConstants.MultiplePackageVersionsListedUnlisted
            );
            const unlistScenario = this.PerformanceService.startScenario(
                PerfScenarios.MultiplePackageVersionsListedUnlisted
            );
            const nuGetPackageDetails: MinimalPackageDetails[] = [];
            payload.selectedVersions.forEach((version: PackageVersion) => {
                nuGetPackageDetails.push({
                    id: this._currentState.selectedPackage.name,
                    version: version.version
                });
            });

            const nuGetBatchRequest: NuGetPackagesBatchRequest = {
                data: { listed: payload.isListed },
                operation: NuGetBatchOperationType.List,
                packages: nuGetPackageDetails
            };
            const nuGetDataService = this.pageContext.getService<IFeedNuGetDataService>("IFeedNuGetDataService");
            const projectId = this._currentState.selectedFeed.project
                ? this._currentState.selectedFeed.project.id
                : null;
            nuGetDataService
                .updatePackageVersions(nuGetBatchRequest, projectId, this._currentState.selectedFeed.id)
                .then(
                    () => {
                        // Lie to the user that they were already listed/unlisted
                        this._currentState.selectedVersions.forEach((version: PackageVersion) => {
                            version.isListed = payload.isListed;

                            if (this._currentState.selectedVersion.id === version.id) {
                                this._currentState.selectedVersion.isListed = payload.isListed;
                            }
                        });

                        this.emitChanged();
                        Actions.PackageListedChangedCompleted.invoke({}); // See ***Note*** above

                        const announcement = buildAnnouncementListedStatusChangedForPackageVersion(
                            payload.selectedVersions,
                            payload.isListed
                        );
                        announce(announcement, true);
                        this.PerformanceService.endScenario(unlistScenario);
                    },
                    error => {
                        Actions.PackageListedChangedCompleted.invoke({}); // See ***Note*** above
                        Actions.ErrorEncountered.invoke({
                            message: error.message
                        } as IError);
                        this.PerformanceService.endScenario(unlistScenario);
                    }
                );
        }
    };

    private _handleToggleCreateBadgePanelAction = (openPanel: boolean): void => {
        this._currentState.showCreateBadgePanel = openPanel;
        this.emitChanged();
    };

    private _handleTogglePromoteDialogAction = (showDialog: boolean) => {
        this._currentState.showPromoteDialog = showDialog;
        this.emitChanged();
    };

    private _handleFeedRetentionPolicyUpdated = (payload: IFeedRetentionPolicyUpdatedPayload): void => {
        if (this._currentState.selectedFeed.id === payload.feedId) {
            this._currentState.selectedFeed.retentionPolicy = payload.retentionPolicy;
            this.emitChanged();
        }
    };

    private handleDismissPackageRetentionMessage = (packageId: string) => {
        this._currentState.retentionMessageSuppressions[packageId] = true;
        this.emitChanged();
    };

    private _lieToUserAfterPackageDeletion(payload: IPackageDeletedPayload): void {
        payload.selectedVersions.forEach((version: PackageVersion) => {
            version.isDeleted = true;
            version.isListed = false;

            if (this._currentState.selectedVersion.id === version.id) {
                this._currentState.selectedVersion.isDeleted = true;
                this._currentState.selectedVersion.isListed = false;
            }
        });
    }

    private _lieToUserAfterRecycleBinAction(payload: IMultiCommandPayload, message: string) {
        payload.selectedVersions.forEach((version: PackageVersion) => {
            version.isDeleted = false;

            if (this._currentState.selectedVersion.id === version.id) {
                this._currentState.selectedVersion.isDeleted = false;
            }
        });

        this._currentState.messageBarText = StringUtils.format(
            message,
            payload.selectedVersions[0].normalizedVersion,
            this._currentState.selectedPackage.name
        );

        // If there is no version left, go back to recycle bin page
        if (!this._currentState.selectedPackage.versions.some(v => v.isDeleted)) {
            const storeService = this.pageContext.getService<IReduxStoreService>("redux-store-service");
            const reduxStore = storeService.getStore(null);
            reduxStore.dispatch(replaceHistoryPoint(FeedHubActions.showRecycleBin()));
            document.title = this._currentState.selectedFeed.fullyQualifiedName;
        }
    }

    private _resetPackageVersions() {
        this._currentState.filteredVersions = null;
        this._versionsFetched = false;
        this._recycleBinVersionsFetched = false;
    }

    private _getHubTitle(): string {
        return StringUtils.format(
            "{0} {1}",
            this._currentState.selectedPackage.name,
            this._currentState.selectedVersion.version
        );
    }

    private get ProtocolProvider(): IFeedProtocolProvider {
        return this.pageContext.getService<IFeedProtocolProvider>("IFeedProtocolProvider");
    }

    private get NavigationService(): IVssHistoryService {
        return this.pageContext.getService<IVssHistoryService>("IVssHistoryService");
    }

    public _versionsFetched: boolean = false;
    public _recycleBinVersionsFetched: boolean = false;
    private _currentState: PackageState;
    private _webPageDataService: HubWebPageDataService;
    private _recycleBinDataService: RecycleBinDataService;
    private _filterByEnabledProtocolDataService: FilterByEnabledProtocolDataService;
    private _feedsDataService: IFeedDataService;
    private _getSelectedFeedDelegate: () => Feed;
    private _getFeedViewsDelegate: () => FeedView[];
    private isAnonymousOrPublic: boolean;
}
